import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyPlayerFeedback({ user }) {
  const { playerId } = useParams();
  const [player, setPlayer] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [newFeedback, setNewFeedback] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isCoach = user?.roles?.includes("academy_coach");
  const isAdmin = ["convenor", "captain"].includes(user?.role);
  const isParent = user?.roles?.includes("academy_parent");
  const canProvideFeedback = isCoach || isAdmin;

  useEffect(() => {
    fetchPlayerAndFeedback();
  }, [playerId]);

  const fetchPlayerAndFeedback = async () => {
    try {
      // Get player details
      const playerResponse = await axios.get(`${API}/academy/players/${playerId}`, {
        withCredentials: true
      });
      setPlayer(playerResponse.data);

      // Get feedback history
      const feedbackResponse = await axios.get(`${API}/academy/players/${playerId}/feedback`, {
        withCredentials: true
      });
      setFeedbackHistory(feedbackResponse.data);
    } catch (error) {
      console.error("Error fetching player feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to load player data");
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (!newFeedback.trim()) {
      toast.error("Please enter feedback text");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/academy/players/${playerId}/feedback`,
        { 
          feedback: newFeedback,
          team_id: selectedTeamId || null
        },
        { withCredentials: true }
      );

      toast.success("Feedback submitted successfully!");
      setNewFeedback("");
      setSelectedTeamId("");
      fetchPlayerAndFeedback();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8">
          <p className="text-slate-400">Player not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <Link to={`/academy/teams/${player.team_id}`}>
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Team
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Player Info */}
        <Card className="p-6 mb-6 bg-slate-900/50 border-2 border-blue-500/30">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {player.child_name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{player.child_name}</h2>
              <p className="text-slate-400">{player.membership_number}</p>
              {player.playing_position && (
                <p className="text-sm text-slate-400 mt-1">Position: {player.playing_position}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Provide Feedback Section - Only for Coaches */}
        {canProvideFeedback && (
          <Card className="p-6 mb-6 bg-gradient-to-br from-slate-900 to-amber-50 border-2 border-white/5">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Provide Feedback
            </h3>
            
            {/* Team Selector - if player is in multiple teams */}
            {player?.teams && player.teams.length > 1 && (
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">Feedback for which team? (Optional)</label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="w-full p-2 border border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">General feedback (all teams)</option>
                  {player.teams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <textarea
              value={newFeedback}
              onChange={(e) => setNewFeedback(e.target.value)}
              placeholder="Share your observations about the player's performance, skills, attitude, and areas for improvement..."
              className="w-full p-4 border border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px] resize-y"
              rows={5}
            />
            <div className="mt-4 flex justify-end">
              <Button
                onClick={submitFeedback}
                disabled={submitting || !newFeedback.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {submitting ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Feedback History */}
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Feedback History</h3>
          
          {feedbackHistory.length === 0 ? (
            <Card className="p-8 text-center bg-slate-900/50">
              <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-400">No feedback yet</p>
              {canProvideFeedback && (
                <p className="text-sm text-slate-400 mt-2">Be the first to provide feedback for this player</p>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {feedbackHistory.map((feedback) => (
                <Card key={feedback.id} className="p-6 bg-slate-900/50 border border-blue-500/30">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {feedback.coach_name?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-white">{feedback.coach_name}</p>
                          {feedback.team_name && (
                            <p className="text-xs text-blue-400 font-medium">
                              📍 {feedback.team_name}
                            </p>
                          )}
                          <p className="text-xs text-slate-400">
                            {new Date(feedback.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <p className="text-slate-300 whitespace-pre-wrap">{feedback.feedback}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Parent View Note */}
        {isParent && (
          <Card className="p-4 mt-6 bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-400">
              💡 <strong>Parent View:</strong> You can view all feedback provided by coaches. This helps you track your child's progress and areas of development.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
